<template>
  <div>
    <b-col v-if="role === 'admin' || role === 'manager'">
      <b-alert
        v-if="errorMessage != null"
        show
        variant="danger"
        dismissible
        fade
        @dismissed="clearError"
      >
        <b-icon
          icon="exclamation-triangle-fill"
          scale="2"
          variant="danger"
        />
        {{ errorMessage }}
      </b-alert>
      <b-form-group
        label="Numéro de bordereau du colis à modifier"
        label-for="BL"
        description="Saisissez le numéro du bordereau du colis pour effectuer une mise à jour rapide du statut. Utilisez le code logistique professionnel."
      >
        <b-input-group class="input-group-merge scan-input-group">
          <b-input-group-prepend is-text>
            <feather-icon icon="MaximizeIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="id"
            placeholder="Numéro du bordereau du colis"
            type="text"
            aria-label="Numéro de bordereau du colis à modifier"
          />
          <b-button
            v-ripple
            :disabled="!id"
            aria-label="Chercher le colis à modifier"
            @click.prevent="checkOrderById"
          >
            <b-icon
              icon="search"
              aria-hidden="true"
            />
            Chercher colis
          </b-button>
          <b-input-group-append>
            <b-button
              :variant="isPhoneScanning ? 'danger' : 'outline-primary'"
              :disabled="isStartingCamera || !canUseCameraScanner"
              class="phone-scanner-button"
              @click="togglePhoneScanner"
            >
              <feather-icon
                :icon="isPhoneScanning ? 'XIcon' : 'CameraIcon'"
                class="mr-1"
              />
              {{ isPhoneScanning ? 'Arrêter' : 'Scanner téléphone' }}
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
      <b-alert
        v-if="!canUseCameraScanner"
        variant="warning"
        show
        class="mb-2"
      >
        Le scan caméra nécessite HTTPS ou localhost sur le téléphone. Utilisez une URL sécurisée ou le champ référence.
      </b-alert>
      <b-card
        v-if="isPhoneScanning || isStartingCamera"
        class="mb-2 phone-scanner-card"
        body-class="p-1"
      >
        <div
          v-if="isStartingCamera"
          class="d-flex align-items-center justify-content-center scanner-loading"
        >
          <b-spinner
            small
            variant="primary"
            class="mr-1"
          />
          Ouverture caméra...
        </div>
        <div
          v-show="isPhoneScanning"
          class="phone-scanner-frame"
        >
          <video
            ref="phoneScannerVideo"
            class="phone-scanner-video"
            autoplay
            muted
            playsinline
          />
          <div class="phone-scanner-target" />
        </div>
      </b-card>
      <b-col class="mt-2">
        <b-overlay
          v-if="!order"
          show
          spinner-variant="primary"
          spinner-type="grow"
          spinner-small
          rounded="sm"
        >
          <b-card
            title="En attente d'un scan de colis..."
            aria-hidden="true"
          >
            <b-card-text>
              Scannez ou saisissez le numéro du bordereau d'un colis pour modifier son statut logistique.
            </b-card-text>
          </b-card>
        </b-overlay>
        <b-card
          v-if="order != null"
          class="my-5"
          aria-label="Modification rapide du statut du colis"
        >
          <b-card-header>
            Modification rapide du statut logistique du colis
          </b-card-header>
          <b-card-body>
            <div class="d-flex justify-content-center mb-3">
              <barcode :value="order.id.toString()" />
            </div>
            <p><strong>Destinataire :</strong> {{ order.name }}</p>
            <p><strong>Adresse de livraison :</strong> {{ order.address }}</p>
            <p><strong>Téléphone principal :</strong> {{ order.phone1 }}</p>
            <p><strong>Montant TTC :</strong> {{ order.price_ttc }}</p>
            <p><strong>Statut logistique :</strong> {{ statusText }}</p>
            <p><strong>Date de création :</strong> {{ order.date }}</p>
            <p><strong>Gouvernorat de destination :</strong> {{ order.governorate }}</p>
            <p><strong>Délégation :</strong> {{ order.delegation }}</p>
            <v-select
              v-model="selectedStatus"
              :options="statusOptions"
              label="name"
              :reduce="status => status.id"
            />
            <b-button
              class="mt-1"
              variant="primary"
              @click="updateStatus"
            >
              <b-icon icon="save" />
              Update Status
            </b-button>
          </b-card-body>
        </b-card>
      </b-col>
    </b-col>
  </div>
</template>

<script>
import {
  BCard, BButton, BFormGroup, BFormInput, BInputGroup, BInputGroupPrepend, BInputGroupAppend, BCol,
  BAlert, BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import VueBarcode from 'vue-barcode'
import store from '@/store/store'
import phoneReferenceScanner from '@/mixins/phoneReferenceScanner'

export default {
  name: 'QuickUpdateOrder',
  mixins: [phoneReferenceScanner],
  components: {
    BButton,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BAlert,
    BSpinner,
    // BFormSelect,
    // BPagination,
    // BTooltip,
    BCard,
    BCol,
    vSelect,
    barcode: VueBarcode,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      phone: null,
      price_ttc: null,
      order: null,
      statusOptions: [
        { id: 2, name: 'Arrivé au dépôt logistique' },
        { id: 3, name: 'En cours d’acheminement' },
        { id: 4, name: 'Livré au destinataire' },
        { id: 5, name: 'Retour au dépôt logistique' },
        { id: 7, name: 'Retour expéditeur' },
        { id: 10, name: 'Retour inter-agence' },
        { id: 11, name: 'Retour inter-agence reçu' },
        { id: 15, name: 'En attente de collecte' },
      ],
      selectedStatus: null,
      isLoading: false,
      errorMessage: null,
      role: '',
      id: null,
    }
  },
  computed: {
    statusText() {
      if (!this.order || typeof this.order.status === 'undefined') return ''
      switch (this.order.status) {
        case 2:
          return 'Arrivé au dépôt logistique'
        case 3:
          return 'En cours d’acheminement'
        case 4:
          return 'Livré au destinataire'
        case 5:
          return 'Retour au dépôt logistique'
        case 7:
          return 'Retour expéditeur'
        case 10:
          return 'Retour inter-agence'
        case 11:
          return 'Retour inter-agence reçu'
        case 15:
          return 'En attente de collecte'
        default:
          return ''
      }
    },
  },
  created() {
    this.role = store.state.user.role

    // Security check - redirect if superadmin (cannot perform rapid edits)
    if (this.role === 'superadmin') {
      this.$toast.warning('Les super administrateurs ne peuvent pas effectuer de modifications rapides')
      this.$router.push({ name: 'order-list' })
    }
  },
  methods: {
    async onPhoneReferenceScanned(scannedRef) {
      this.id = scannedRef
      await this.checkOrderById()
      this.stopPhoneScanner()
    },
    reset() {
      this.order = null
      this.phone = null
      this.price_ttc = null
      this.selectedStatus = null
    },
    async checkOrder() {
      try {
        this.isLoading = true
        const { data } = await axios.get('/api/orders/ready-for/quick-update', {
          params: {
            phone: this.phone,
            price_ttc: this.price_ttc,
          },
        })
        this.order = data
        if (this.order != null) {
          this.errorMessage = null
        }
        this.selectedStatus = data.status
        this.price_ttc = null
        this.phone = null
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        const [errorMessage] = error.response.data
        this.errorMessage = errorMessage
        this.showToast('danger', 'top-center', error.toString())
      }
    },
    async checkOrderById() {
      try {
        this.isLoading = true
        const { data } = await axios.get('/api/orders/orders/quick-update/by-id/', {
          params: {
            order_id: this.id,
          },
        })
        this.order = data
        if (this.order != null) {
          this.errorMessage = null
        }
        this.selectedStatus = data.status
        this.id = null
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        // Enhanced error handling for roadmap status
        let errorMsg = ''
        if (error.response && error.response.data) {
          if (Array.isArray(error.response.data)) {
            [errorMsg] = error.response.data
          } else if (typeof error.response.data === 'object' && error.response.data.error) {
            errorMsg = error.response.data.error
          } else {
            errorMsg = JSON.stringify(error.response.data)
          }
        } else {
          errorMsg = error.toString()
        }
        this.errorMessage = errorMsg
        this.showToast('danger', 'top-center', errorMsg)
      }
    },
    async updateStatus() {
      const payload = {
        status: this.selectedStatus,
      }
      try {
        this.isLoading = true
        await axios.put(`/api/orders/quick-update/${this.order.id}/`, payload)
        this.showToast('success', 'top-center', 'Le statut du colis a été mis a jours avec success')
        this.reset()
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        // Enhanced error handling for roadmap status
        let errorMsg = ''
        if (error.response && error.response.data) {
          if (Array.isArray(error.response.data)) {
            [errorMsg] = error.response.data
          } else if (typeof error.response.data === 'object' && error.response.data.error) {
            errorMsg = error.response.data.error
          } else {
            errorMsg = JSON.stringify(error.response.data)
          }
        } else {
          errorMsg = error.toString()
        }
        this.errorMessage = errorMsg
        this.showToast('danger', 'top-center', errorMsg)
      }
    },
    clearError() {
      this.errorMessage = null
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>
  <style lang="scss" >
  @import "@core/scss/vue/libs/vue-select.scss";
  @import '@core/scss/vue/libs/vue-good-table.scss';
  // CSS
  .custom-danger {
    background-color: #ff5252;
    color: #fff;
  }

  .alert-icon {
    margin-right: 8px;
    display: inline-flex;
    align-items: center;
    font-size: 18px;
    line-height: 1;
  }

  .phone-scanner-button {
    white-space: nowrap;
  }

  .phone-scanner-card {
    width: 100%;
    max-width: 520px;
  }

  .scanner-loading {
    min-height: 220px;
  }

  .phone-scanner-frame {
    position: relative;
    overflow: hidden;
    width: 100%;
    max-width: 520px;
    aspect-ratio: 4 / 3;
    background: #111;
    border-radius: 6px;
  }

  .phone-scanner-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .phone-scanner-target {
    position: absolute;
    top: 18%;
    right: 18%;
    bottom: 18%;
    left: 18%;
    border: 2px solid rgba(255, 255, 255, 0.9);
    border-radius: 6px;
    box-shadow: 0 0 0 999px rgba(0, 0, 0, 0.22);
    pointer-events: none;
  }

  @media (max-width: 575.98px) {
    .scan-input-group {
      align-items: stretch;
      flex-wrap: wrap;
    }

    .scan-input-group > .input-group-append {
      flex: 0 0 100%;
      margin-top: 0.5rem;
      margin-left: 0;
    }

    .scan-input-group > .input-group-append .btn,
    .phone-scanner-button {
      width: 100%;
      border-radius: 0.357rem;
    }

    .phone-scanner-frame {
      max-width: none;
      aspect-ratio: 3 / 4;
    }
  }
  </style>
